import { StudyStoreState } from '@/enums/study-store-state';
import { ColorLevel, WidgetTypes } from 'interfaces';
import { IStudyStore } from '@/interfaces/store/study-store/study-store.store.interface';
import ITrackingObject from '@/interfaces/tracking-object.interface';
import { store } from '@/store/store';
import globals from '@/utils/globals';
import ImpressionTracker from '@/utils/impression-tracker';
import { FitLevel } from 'interfaces';
import { ScoreDeterminantType } from '@studyportals/bestfit-api-fe-client';
import { Action, Product } from '@studyportals/datalake-event-tracker';
import { OnMounted } from '@studyportals/vue-platform';
import { computed, watch, ref, Ref } from 'vue';

export default class Widget implements OnMounted {
	public initialAnimationTimeout = 45000;
	public animationInterval = 45000;
	public isAnimated = ref(false);
	public canAnimate = ref(false);
	public moduleReady = ref(false);

	public widget: Ref<HTMLDivElement | null> = ref(null);
	private impressionTracker?: ImpressionTracker;

	public hasEmptyText = computed((): boolean => {
		return this.emptyText ? true : false;
	});

	public userLoggedIn = computed((): boolean => {
		return store.getters.userLoggedIn();
	});

	public bestFitCompleteness = computed((): number | null => {
		return store.getters.bestFitCompleteness();
	});

	public currentStudyStore = computed((): IStudyStore | null => {
		return store.getters.getStudyStore(this.studyId);
	});

	public storeState = computed((): StudyStoreState | null => {
		return this.currentStudyStore.value?.getters.storeState() ?? null;
	});

	public bestFitScore = computed((): number | null => {
		return (
			this.currentStudyStore.value?.getters.scoreByDeterminant(
				ScoreDeterminantType.OVERALL_FIT,
			) ?? null
		);
	});

	public displayScore = computed((): FitLevel | string => {
		const scoreObject = this.currentStudyStore.value?.getters.displayScore(
			this.bestFitScore.value!,
		);

		return store.getters.scoreText(scoreObject!.score) ?? '';
	});

	public fitColor = computed((): ColorLevel | null => {
		if (this.isFullProfile.value && this.bestFitScore.value) {
			return store.getters.scoreColor(this.bestFitScore.value);
		}

		return null;
	});

	public isEmptyProfile = computed((): boolean => {
		return this.bestFitCompleteness.value === 0;
	});

	public isFillingProfile = computed((): boolean => {
		return (
			this.bestFitCompleteness.value! < 100 &&
			this.bestFitCompleteness.value! > 0
		);
	});

	public isFullProfile = computed((): boolean => {
		return this.bestFitCompleteness.value === 100;
	});

	public calculationFailed = computed((): boolean => {
		if (this.storeState.value === StudyStoreState.FAILED) {
			return true;
		}

		return false;
	});

	public calculationReady = computed((): boolean => {
		if (this.storeState.value === StudyStoreState.CALCULATED) {
			return true;
		}

		return false;
	});

	constructor(
		protected studyId: number,
		protected widgetType: WidgetTypes,
		protected widgetLocation: string,
		protected emptyText: string,
		protected determinantType?: ScoreDeterminantType,
	) {
		document.addEventListener('trigger_action_animations', () => {
			this.canAnimate.value = true;
		});
	}

	mounted(): void {
		this.waitForSession();
	}

	public incentiveClicked(event?: Event): void {
		event?.preventDefault();

		store.mutations.setActiveStudyId(this.studyId);
		store.actions.incentiveClicked();
		this.trackIncentiveClick();
	}

	public incentiveClickedRedirect(event?: Event): void {
		event?.preventDefault();
		event?.stopPropagation();

		this.trackIncentiveClick();

		const parentCard = (event?.target as Element)?.closest(
			'.js-bestFitStudycard',
		);

		if (!parentCard) {
			return;
		}

		window.location.href = this.transformUrl(parentCard?.getAttribute('href'));
	}

	private transformUrl(url: string | null): string {
		if (!url) {
			return '';
		}

		const studypageUrl = new URL(url);
		studypageUrl.searchParams.set('openbestfit', 'true');

		return studypageUrl.toString();
	}

	protected waitForSession(): void {
		if (!this.userLoggedIn.value) {
			watch(this.userLoggedIn, () => this.waitForModuleReady());
		}

		this.waitForModuleReady();
	}

	private waitForModuleReady(): void {
		if (
			this.storeState.value === StudyStoreState.READY ||
			this.storeState.value === StudyStoreState.CALCULATED ||
			this.storeState.value === StudyStoreState.FAILED
		) {
			this.handleModuleReady();
			this.trackImpression();
			return;
		}

		watch(this.storeState, () => {
			if (
				this.storeState.value !== StudyStoreState.CREATED &&
				this.storeState.value !== StudyStoreState.PENDING
			) {
				this.handleModuleReady();
				this.trackImpression();
			}
		});
	}

	private handleModuleReady(): void {
		globals.eventBus.emit('moduleReady', this.studyId);
		this.moduleReady.value = true;
	}

	public trackIncentiveClick(): void {
		const trackingParams: ITrackingObject = {
			category: Product.BESTFIT_OVERVIEW,
			action: Action.CLICK,
			label: `widget_${this.widgetType}@${this.widgetLocation}`,
			property: String(this.studyId),
			value: this.bestFitScore.value!,
		};

		globals.bestFitTracking.snowPlow(trackingParams);
	}

	private trackImpression(): void {
		const element = this.widget.value?.querySelector('.js-Widget');

		if (!this.widget.value || !element) return;

		if (!this.impressionTracker) {
			this.impressionTracker = new ImpressionTracker(
				this.widget.value,
				this.widgetType,
				this.widgetLocation,
				this.studyId,
				this.bestFitScore,
			);
		}

		this.impressionTracker?.processElement();
	}

	public animateIcon(): void {
		setTimeout(() => {
			if (this.canAnimate) this.toggleAnimation();

			setInterval(() => {
				if (this.canAnimate) this.toggleAnimation();
			}, this.animationInterval);
		}, this.initialAnimationTimeout);
	}

	private toggleAnimation(): void {
		this.isAnimated.value = true;

		setTimeout(() => {
			this.isAnimated.value = false;
		}, 1200);
	}
}
